import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Member from "./Member";
import { LabeledCheckbox } from "../LabeledCheckbox";
import {
  getDisplayGlobal,
  getHideMembersWithEnoughShiftsHours,
  getHiddenCount,
  getLoading,
  hasEnoughShiftHours,
  getMemberShiftsCount,
  getMemberHoursCount,
} from "../../stores/member/memberSelectors";
import {
  resetFilters,
  setDisplayGlobal,
  setFilter,
  setHiddenCount,
} from "../../stores/member/memberActions";
import i18n from "../../utils/i18n";

const MemberList = ({ members, periods }) => {
  const dispatch = useDispatch();
  const displayGlobal = useSelector(getDisplayGlobal);
  const hideMembersWithEnoughShiftsHours = useSelector(
    getHideMembersWithEnoughShiftsHours
  );
  const hiddenCount = useSelector(getHiddenCount);
  const loading = useSelector(getLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [periodId, setPeriodId] = useState("0");
  const [sorting, setSorting] = useState("name_asc");

  useEffect(() => {
    dispatch(resetFilters());
  }, [dispatch]);

  const handleSearchInput = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setPeriodId(value);
  };

  const handleSortingChange = (e) => {
    const value = e.target.value;
    setSorting(value);
  };

  const handleDisplayChange = (e) => {
    dispatch(setDisplayGlobal(e.target.checked));
  };

  const handleHideEnoughMembers = (e) => {
    dispatch(
      setFilter("hide_members_with_enough_shifts_hours", e.target.checked)
    );
  };

  const isMemberVisible = (member) => {
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      if (!member.name.toLowerCase().includes(lowerSearch)) return false;
    }
    if (periodId !== "0") {
      if (member.period_id !== parseInt(periodId, 10)) return false;
    }
    if (hideMembersWithEnoughShiftsHours) {
      if (hasEnoughShiftHours(member)) return false;
    }
    return true;
  };

  const visibleMemberIds = useMemo(() => {
    return new Set(members.filter(isMemberVisible).map((m) => m.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, searchTerm, periodId, hideMembersWithEnoughShiftsHours]);

  const sortedMembers = useMemo(() => {
    const sorted = [...members];
    sorted.sort((a, b) => {
      switch (sorting) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "hours_asc":
          return (
            getMemberHoursCount(a, displayGlobal) -
            getMemberHoursCount(b, displayGlobal)
          );
        case "hours_desc":
          return (
            getMemberHoursCount(b, displayGlobal) -
            getMemberHoursCount(a, displayGlobal)
          );
        case "shifts_asc":
          return (
            getMemberShiftsCount(a, displayGlobal) -
            getMemberShiftsCount(b, displayGlobal)
          );
        case "shifts_desc":
          return (
            getMemberShiftsCount(b, displayGlobal) -
            getMemberShiftsCount(a, displayGlobal)
          );
        case "missing_desc": {
          const aMissing =
            (a.required_hours || 0) - getMemberHoursCount(a, displayGlobal);
          const bMissing =
            (b.required_hours || 0) - getMemberHoursCount(b, displayGlobal);
          return bMissing - aMissing;
        }
        default:
          return 0;
      }
    });
    return sorted;
  }, [members, sorting, displayGlobal]);

  // Update hidden count when filtering changes
  useEffect(() => {
    if (hideMembersWithEnoughShiftsHours) {
      const withEnoughCount = members.filter((member) => {
        let passesOtherFilters = true;
        if (searchTerm) {
          passesOtherFilters =
            passesOtherFilters &&
            member.name.toLowerCase().includes(searchTerm.toLowerCase());
        }
        if (periodId !== "0") {
          passesOtherFilters =
            passesOtherFilters &&
            member.period_id === parseInt(periodId, 10);
        }
        return passesOtherFilters && hasEnoughShiftHours(member);
      });
      dispatch(setHiddenCount(withEnoughCount.length));
    } else {
      dispatch(setHiddenCount(0));
    }
  }, [
    members,
    searchTerm,
    periodId,
    hideMembersWithEnoughShiftsHours,
    dispatch,
  ]);

  const periodOptions = useMemo(() => {
    const options = periods.map((period) => (
      <option key={period.id} value={period.id}>
        {period.name}
      </option>
    ));
    options.unshift(
      <option key="0" value="0">
        {i18n.t("schedule.members.filter.all_periods")}
      </option>
    );
    return options;
  }, [periods]);

  const sortingOptions = [
    "name_asc",
    "name_desc",
    "hours_asc",
    "hours_desc",
    "shifts_asc",
    "shifts_desc",
    "missing_desc",
  ].map((sortOption) => (
    <option key={sortOption} value={sortOption}>
      {i18n.t(`schedule.members.sorting.${sortOption}`)}
    </option>
  ));

  let memberNodes = sortedMembers.map((member) => {
    const hidden = !visibleMemberIds.has(member.id);
    return (
      <div key={member.id} style={hidden ? { display: "none" } : undefined}>
        <Member member={member} />
      </div>
    );
  });

  if (visibleMemberIds.size === 0 && loading) {
    memberNodes = (
      <div className="loading_container">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <div className="memberList">
      <input
        id="search_input"
        className="form-control"
        type="text"
        placeholder="Search for member"
        value={searchTerm}
        onChange={handleSearchInput}
      />

      {periodOptions.length > 1 && (
        <div>
          <br />
          <select
            id="period_select"
            className="form-control"
            value={periodId}
            onChange={handlePeriodChange}
          >
            {periodOptions}
          </select>
        </div>
      )}

      <br />
      <select
        id="sorting_select"
        className="form-control"
        value={sorting}
        onChange={handleSortingChange}
      >
        {sortingOptions}
      </select>

      <div style={{ marginTop: "10px" }}>
        <LabeledCheckbox
          onChange={handleDisplayChange}
          checked={displayGlobal}
          labelText={i18n.t("schedule.members.filter.display_global")}
        />
        <LabeledCheckbox
          onChange={handleHideEnoughMembers}
          id="hide_members_with_enough_shifts_hours"
          checked={hideMembersWithEnoughShiftsHours}
          labelText={i18n.t(
            "schedule.members.filter.hide_members_with_enough_shifts_hours"
          )}
        />
        {hideMembersWithEnoughShiftsHours && (
          <p>
            <small>
              {i18n.t(
                "schedule.members.filter.display_message_for_hidden_members",
                {
                  count: hiddenCount,
                }
              )}
            </small>
          </p>
        )}
      </div>
      <hr />
      {memberNodes}
    </div>
  );
};

export default MemberList;
